export interface AppConfig {
  API_BASE_URL: string;
  API_VIRTUAL_DIRECTORY_URL: string;
  RAZORPAY_KEY?: string;
  RAZORPAY_URL?: string;
  GOOGLE_CLIENT_ID?: string;
  GOOGLE_MAP_API_KEY?: string;
  WORDPRESS_URL?: string;
}

export class ApiConfig {
  private static instance: ApiConfig;
  private config: AppConfig | null = null;

  private constructor() {}

  // Singleton
  public static getInstance(): ApiConfig {
    if (!ApiConfig.instance) {
      ApiConfig.instance = new ApiConfig();
    }
    return ApiConfig.instance;
  }

  // Load configuration dynamically at runtime, falling back to environment variables
  public async load(): Promise<void> {
    let externalConfig: Partial<AppConfig> = {};
    try {
      const response = await fetch("/config.json");
      if (response.ok) {
        const json = await response.json();
        externalConfig = {
          API_BASE_URL: json.REACT_APP_API_BASE_URL,
          API_VIRTUAL_DIRECTORY_URL: json.REACT_APP_API_VIRTUAL_DIRECTORY_URL,
          RAZORPAY_KEY: json.REACT_APP_RAZORPAY_KEY,
          RAZORPAY_URL: json.REACT_APP_RAZORPAY_URL,
          GOOGLE_CLIENT_ID: json.REACT_APP_GOOGLE_CLIENT_ID,
          GOOGLE_MAP_API_KEY: json.REACT_APP_GOOGLEMAPAPIKEY,
          WORDPRESS_URL: json.REACT_APP_WORDPRESS_URL,
        };
      }
    } catch (e) {
      console.warn(
        "Could not load external config.json, falling back to process.env:",
        e,
      );
    }

    this.config = {
      API_BASE_URL:
        externalConfig.API_BASE_URL || process.env.REACT_APP_API_BASE_URL || "",
      API_VIRTUAL_DIRECTORY_URL:
        externalConfig.API_VIRTUAL_DIRECTORY_URL ||
        process.env.REACT_APP_API_VIRTUAL_DIRECTORY_URL ||
        "",
      RAZORPAY_KEY:
        externalConfig.RAZORPAY_KEY || process.env.REACT_APP_RAZORPAY_KEY,
      RAZORPAY_URL:
        externalConfig.RAZORPAY_URL || process.env.REACT_APP_RAZORPAY_URL,
      GOOGLE_CLIENT_ID:
        externalConfig.GOOGLE_CLIENT_ID ||
        process.env.REACT_APP_GOOGLE_CLIENT_ID,
      GOOGLE_MAP_API_KEY:
        externalConfig.GOOGLE_MAP_API_KEY ||
        process.env.REACT_APP_GOOGLEMAPAPIKEY,
      WORDPRESS_URL:
        externalConfig.WORDPRESS_URL ||
        process.env.REACT_APP_WORDPRESS_URL ||
        "https://demo.ticktech.events",
    };

    // Dynamically load WordPress URL from backend API configuration if available
    if (this.config.API_BASE_URL) {
      try {
        const cleanApiBaseUrl = this.config.API_BASE_URL.replace(/\/+$/, "");
        const apiResponse = await fetch(
          `${cleanApiBaseUrl}/PublicBooking/config`,
        );
        if (apiResponse.ok) {
          const apiJson = await apiResponse.json();
          const fetchedUrl =
            apiJson?.data?.wordpressUrl || apiJson?.wordpressUrl;
          if (fetchedUrl) {
            this.config.WORDPRESS_URL = fetchedUrl;
          }
        }
      } catch (e) {
        console.warn(
          "Could not load WordPress URL from backend API, using local configuration:",
          e,
        );
      }
    }

    // Basic validation
    if (!this.config.API_BASE_URL || !this.config.API_VIRTUAL_DIRECTORY_URL) {
      throw new Error(
        "Missing API_BASE_URL or API_VIRTUAL_DIRECTORY_URL in configuration",
      );
    }

    this.config = Object.freeze(this.config);
  }

  // Internal guard
  private ensure(): AppConfig {
    if (!this.config) {
      throw new Error(
        "[ApiConfig] Configuration not loaded. Call load() first.",
      );
    }
    return this.config;
  }

  // ---- Getters ----
  public get apiUrl(): string {
    const { API_BASE_URL } = this.ensure();
    return API_BASE_URL.replace(/\/+$/, "");
  }

  // Keep original name for compatibility with existing code
  public get VirtualdirectoryUrl(): string {
    const { API_VIRTUAL_DIRECTORY_URL } = this.ensure();
    return API_VIRTUAL_DIRECTORY_URL.replace(/\/+$/, "");
  }

  public get razorpayKey(): string {
    const { RAZORPAY_KEY } = this.ensure();
    return (RAZORPAY_KEY || "").trim();
  }

  public get razorpayUrl(): string {
    const { RAZORPAY_URL } = this.ensure();
    // default to official CDN if not provided
    return (
      RAZORPAY_URL || "https://checkout.razorpay.com/v1/checkout.js"
    ).trim();
  }

  public get googleClientId(): string {
    if (!this.config) throw new Error("[ApiConfig] Call load() first.");
    const v = this.config.GOOGLE_CLIENT_ID || "";
    if (!v)
      throw new Error("[ApiConfig] GOOGLE_CLIENT_ID missing in configuration");
    return v;
  }

  public get googleMapApiKey(): string {
    const { GOOGLE_MAP_API_KEY } = this.ensure();
    return (GOOGLE_MAP_API_KEY || "").trim();
  }

  public get wordpressUrl(): string {
    const { WORDPRESS_URL } = this.ensure();
    return (WORDPRESS_URL || "https://demo.ticktech.events").replace(
      /\/+$/,
      "",
    );
  }
}
